import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { SafeArea } from "@/components/ios/SafeArea";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp, Brain, Calendar, Search, Target } from "lucide-react";
import { cn } from "@/lib/utils";
import { ColonySelector } from "@/components/ColonySelector";
import { ScreenHeader } from "@/components/ios/ScreenHeader";
import { useChatWithPersistence } from "@/hooks/useChatWithPersistence";
import { useColonies } from "@/hooks/useColonies";
import { sessionPersistence } from "@/utils/sessionPersistence";
import { ChatMessage } from "@/components/ChatMessage";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Composer } from "@/components/chat/Composer";
import { useColonyContext } from "@/hooks/useColonyContext";
 

interface QuickStartTile {
  id: string;
  icon: React.ElementType;
  title: string;
  description: string;
  prompt: string;
}

interface AltMeResponse {
  text: string;
  traceId?: string;
  [key: string]: unknown;
}

const getQuickStartTiles = (hasColonies: boolean): QuickStartTile[] => [
  ...(hasColonies ? [{
    id: "colony-status",
    icon: Brain,
    title: "Check Colony Status",
    description: "See what your colonies are working on now.",
    prompt: "Show me what all my colonies are working on right now."
  }] : [{
    id: "create-colony",
    icon: Brain,
    title: "Create Prime Colony",
    description: "Let's set up your first AI colony together.",
    prompt: "Help me create my first colony. What should we focus on?"
  }]),
  {
    id: "plan-day",
    icon: Calendar,
    title: "Plan My Day",
    description: "Let's map out your priorities together.",
    prompt: "Help me plan my day and prioritize what's most important."
  },
  {
    id: "research",
    icon: Search,
    title: "Research Something",
    description: "Ask me to find, analyze, or summarize.",
    prompt: "I need you to research and analyze something for me."
  },
  {
    id: "work-goal",
    icon: Target,
    title: "Work Toward My Goal",
    description: "Progress check + next steps for your goals.",
    prompt: "Let's review my goals and figure out the next steps to make progress."
  }
];

export default function ControlRoom() {
  const navigate = useNavigate();
  const { colonies, hasColonies } = useColonies();
  const { activeColonyId } = useColonyContext();
  const [inputValue, setInputValue] = useState("");
  const [isQuickActionsOpen, setIsQuickActionsOpen] = useState(true);
  const autoCollapsedRef = useRef<boolean>(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const localScrollRef = useRef<{ isAutoScrolling: boolean; lastHeight: number }>({ isAutoScrolling: false, lastHeight: 0 });

  const targetColonyId = useMemo(
    () => activeColonyId ?? colonies?.[0]?.id ?? null,
    [activeColonyId, colonies]
  );

  // Gate render until we have a real id
  if (!targetColonyId) {
    console.log("[control-room] waiting for cid");
    return <div className="p-6 text-muted-foreground">Loading your colony…</div>;
  }
  
  console.log("[control-room] cid", targetColonyId);
  const storageKey = `col:${targetColonyId}:altme:control-room:v1`;
  const chat = useChatWithPersistence({ storageKey, keepAlive: true });

  // One-time greeting per colony (persist a "greeted" flag alongside the thread key)
  useEffect(() => {
    const greetedKey = `${storageKey}:greeted`;
    if (localStorage.getItem(greetedKey) === "1") return;

    (async () => {
      try {
        const { firebaseAuthService } = await import("@/../frontend/services/firebaseAuth.service");
        const user = await firebaseAuthService.getCurrentUser();
        const { api } = await import("@/lib/api/client");
        const res = await api.post<AltMeResponse>(`/api/colony/${targetColonyId}/interact`, {
          userId: user?.uid, mode: "chat", brainId: "alt-me", orchestrate: true,
          history: [{ role: "user", content: "Please greet the user by name and offer help." }],
          profile: { name: user?.displayName || "", email: user?.email || "" }
        });
        const reply = res?.text || (res as any)?.envelope?.narration;
        chat.appendAssistant(reply || "Welcome back. What would you like to work on today?");
        localStorage.setItem(greetedKey, "1");
      } catch {
        chat.appendAssistant("Welcome back. What would you like to work on today?");
      }
    })();
  }, [targetColonyId, storageKey]);


  const handleTileClick = async (tile: QuickStartTile) => {
    const text = tile.prompt.trim();
    if (!text || chat.isSending) return;
    try {
      const { firebaseAuthService } = await import("@/../frontend/services/firebaseAuth.service");
      const user = await firebaseAuthService.getCurrentUser();
      await chat.send(text, {
        endpoint: `/api/colony/${targetColonyId}/interact`,
        body: {
          userId: user?.uid,
          mode: "chat",
          brainId: "alt-me",
          orchestrate: true,
          messages: [{ role: "user", content: text }],
          profile: { name: user?.displayName || "", email: user?.email || "" }
        }
      });
    } catch {
      await chat.send(text, {
        endpoint: `/api/colony/${targetColonyId}/interact`,
        body: { mode: "chat", brainId: "alt-me", orchestrate: true, messages: [{ role: "user", content: text }] }
      });
    }
  };

  const handleSendMessage = async () => {
    const text = inputValue.trim();
    if (!text || chat.isSending) return;
    setInputValue("");
    try {
      const { firebaseAuthService } = await import("@/../frontend/services/firebaseAuth.service");
      const user = await firebaseAuthService.getCurrentUser();
      await chat.send(text, {
        endpoint: `/api/colony/${targetColonyId}/interact`,
        body: {
          userId: user?.uid,
          mode: "chat",
          brainId: "alt-me",
          orchestrate: true,
          messages: [{ role: "user", content: text }],
          profile: { name: user?.displayName || "", email: user?.email || "" }
        }
      });
    } catch {
      await chat.send(text, {
        endpoint: `/api/colony/${targetColonyId}/interact`,
        body: { mode: "chat", brainId: "alt-me", orchestrate: true, messages: [{ role: "user", content: text }] }
      });
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleBackToLanding = () => {
    navigate("/");
  };

  // Update session persistence (colony + route)
  useEffect(() => {
    sessionPersistence.setLastActive(targetColonyId, "/control-room");
  }, [targetColonyId]);

  // (Redundant, but safe) Persist active colony for navigation continuity
  useEffect(() => {
    if (targetColonyId) {
      try { sessionPersistence.setLastActive(targetColonyId, "/control-room"); } catch { /* noop */ }
    }
  }, [targetColonyId]);

  // Auto-collapse Quick Actions once after the first message, but do NOT fight user toggles
  useEffect(() => {
    if (!autoCollapsedRef.current && chat.messages.length > 0 && isQuickActionsOpen) {
      setIsQuickActionsOpen(false);
      autoCollapsedRef.current = true;
    }
  }, [chat.messages.length, isQuickActionsOpen]);

  // Smooth auto-scroll to bottom on new messages/typing, without fighting user scroll
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const threshold = 120; // px from bottom considered "at bottom"
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const isAtBottom = distanceFromBottom < threshold;
    if (!isAtBottom && !localScrollRef.current.isAutoScrolling) return;
    localScrollRef.current.isAutoScrolling = true;
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    const t = setTimeout(() => { localScrollRef.current.isAutoScrolling = false; }, 350);
    return () => clearTimeout(t);
  }, [chat.messages.length, chat.isTyping]);


  return (
    <SafeArea>
      {/* Fixed Header - Never scrolls */}
      <ScreenHeader
        title="Control Room"
        right={hasColonies ? <ColonySelector variant="minimal" currentColonyId={targetColonyId} /> : undefined}
        onBack={handleBackToLanding}
      />

      {/* Scrollable Content Area - Only this scrolls */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto scroll-pb-chat min-h-0 p-6 space-y-6">
          {/* Show empty state info if no colonies, but keep chat available */}
          {!hasColonies && (
            <div className="bg-card border border-ios-separator rounded-xl p-4 mb-4">
              <div className="flex items-center gap-3 mb-2">
                <Brain size={20} className="text-primary" />
                <h3 className="ios-body font-semibold text-card-foreground">No Colonies Yet</h3>
              </div>
              <p className="ios-callout text-muted-foreground">
                Start by chatting with Alt-me to create your first colony or get help with anything.
              </p>
            </div>
          )}

          {/* Quick Actions - Collapsible */}
          <Collapsible open={isQuickActionsOpen} onOpenChange={setIsQuickActionsOpen}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="w-full justify-between p-0 h-auto hover:bg-transparent">
                <h3 className="ios-headline font-semibold text-foreground">
                  Quick Actions
                </h3>
                {isQuickActionsOpen ? (
                  <ChevronUp size={20} className="text-muted-foreground" />
                ) : (
                  <ChevronDown size={20} className="text-muted-foreground" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-4 mt-4 overflow-visible">
              <div className="grid grid-cols-1 gap-4">
                {getQuickStartTiles(hasColonies).map((tile) => {
                    const IconComponent = tile.icon;
                    return (
                      <button
                        key={tile.id}
                        onClick={() => handleTileClick(tile)}
                        disabled={chat.isSending}
                        className={cn(
                          "group p-5 rounded-xl bg-card border border-ios-separator hover:bg-accent/30 transition-all duration-200",
                          "active:scale-[0.98] text-left w-full disabled:opacity-50 disabled:pointer-events-none",
                          "shadow-sm hover:shadow-md"
                        )}
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl bg-primary/10 group-hover:bg-primary/20 flex items-center justify-center shrink-0 transition-colors">
                            <IconComponent size={20} className="text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="ios-body font-semibold text-card-foreground mb-2">
                              {tile.title}
                            </h4>
                            <p className="ios-callout text-muted-foreground leading-relaxed">
                              {tile.description}
                            </p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* Chat Messages */}
            {chat.messages.map((message) => (
              <ChatMessage
                key={message.id}
                message={message}
                brain={message.sender === "assistant" ? {
                  id: "alt-me",
                  name: "Alt-me",
                  role: "AI Orchestrator",
                  color: "#6366f1",
                  status: "active" as const,
                  colonyId: targetColonyId
                } : undefined}
              />
            ))}
            {chat.isTyping && (
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center shrink-0">
                  <Brain size={20} className="text-primary-foreground animate-pulse" />
                </div>
                <div className="flex-1">
                  <div className="mb-2">
                    <span className="ios-body font-medium text-foreground">Alt-me</span>
                    <span className="text-xs text-muted-foreground ml-2">Typing...</span>
                  </div>
                  <div className="bg-chat-assistant rounded-2xl px-4 py-3">
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" />
                      <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                      <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                    </div>
                  </div>
                </div>
              </div>
            )}
        </div>

      {/* Fixed Composer - Never scrolls */}
      <Composer
        value={inputValue}
        onChange={setInputValue}
        onSend={handleSendMessage}
        canSend={!!inputValue.trim() && !chat.isSending}
        isSending={chat.isSending}
        placeholder="Ask Alt-me anything…"
      />
    </SafeArea>
  );
}
